/**
 * Sign in.
 *
 * Two ways in, and the PIN pad is the default, because the person signing in
 * most often is a cashier at a touchscreen with no keyboard in reach. The
 * password form is one tap away for the owner and the back office.
 *
 * The business name is fetched before anyone signs in - it is the one piece of
 * reassurance that this is the right till, and the /bootstrap endpoint is
 * deliberately public for it.
 */

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiLock, FiUser, FiDelete, FiArrowRight, FiAlertCircle } from 'react-icons/fi';

import { login, loginWithPin, selectAuth, clearError } from '../store/authSlice';
import { authApi } from '../services/endpoints';
import Spinner from '../components/ui/Spinner';
import AppFooter from '../components/ui/AppFooter';
import posLogo from '../assets/POS_Logo.jpeg';

const PIN_LENGTH_MAX = 6;

const LoginPage = () => {
    const dispatch = useDispatch();
    const { status, error } = useSelector(selectAuth);
    const busy = status === 'loading';

    const [mode, setMode] = useState('pin');
    const [pin, setPin] = useState('');
    const [form, setForm] = useState({ userName: '', password: '' });
    const [branding, setBranding] = useState(null);

    useEffect(() => {
        authApi.bootstrap()
            .then(setBranding)
            // A failure here means the server is not up; the sign-in attempt
            // will say so more clearly than a banner at this point would.
            .catch(() => setBranding(null));
    }, []);

    useEffect(() => {
        if (error) {
            setPin('');
            const timer = setTimeout(() => dispatch(clearError()), 5000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [error, dispatch]);

    const pressPin = (digit) => {
        if (busy) return;

        if (digit === 'back') {
            setPin((current) => current.slice(0, -1));
            return;
        }
        if (pin.length >= PIN_LENGTH_MAX) return;

        const next = pin + digit;
        setPin(next);

        // Four digits is the common length, so submit automatically - a cashier
        // between orders should not need a second tap. A longer PIN is entered
        // with the arrow button.
        if (next.length === 4) {
            dispatch(loginWithPin({ pin: next }));
        }
    };

    return (
        <div className="flex h-full items-center justify-center overflow-y-auto
                        bg-gradient-to-br from-brand-950 via-brand-900 to-slate-900 p-4">
            <div className="my-auto w-full max-w-md py-2">
                {/* Brand */}
                <div className="mb-4 text-center sm:mb-6">
                    {/* The product mark on its own white tile - the artwork has
                        a white ground, so it would read as a box on the
                        gradient anyway. Cropped to the middle band of the
                        square file, which is where the mark is. */}
                    <div className="mx-auto mb-3 h-20 w-36 overflow-hidden rounded-2xl bg-white shadow-panel
                                    sm:h-24 sm:w-44">
                        <img src={posLogo} alt="Awami POS" className="h-full w-full object-cover" />
                    </div>
                    <h1 className="flex items-center justify-center gap-2 text-xl font-semibold text-white sm:text-2xl">
                        {branding?.logoPath && (
                            <img src={branding.logoPath} alt="" className="h-7 w-7 shrink-0 rounded-md object-contain" />
                        )}
                        {branding?.businessName || 'Point of Sale'}
                    </h1>
                    <p className="mt-1 text-sm text-white/60">
                        {mode === 'pin' ? 'Enter your PIN to start' : 'Sign in to continue'}
                    </p>
                </div>

                <div className="rounded-2xl bg-white p-4 shadow-panel sm:p-5">
                    {error && (
                        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                            <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {mode === 'pin' ? (
                        <>
                            {/* PIN dots */}
                            <div className="mb-4 flex justify-center gap-3 sm:mb-5">
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <span
                                        key={index}
                                        className={`h-3.5 w-3.5 rounded-full transition-colors
                                                    ${index < pin.length ? 'bg-brand-600' : 'bg-slate-200'}`}
                                    />
                                ))}
                                {pin.length > 4 && (
                                    <span className="tabular text-sm text-slate-500">+{pin.length - 4}</span>
                                )}
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                                    <button
                                        key={digit}
                                        type="button"
                                        disabled={busy}
                                        onClick={() => pressPin(digit)}
                                        className="btn min-h-[3rem] sm:min-h-[3.5rem] border border-slate-200 bg-slate-50 text-xl font-semibold text-slate-800 hover:bg-slate-100 active:bg-slate-200"
                                    >
                                        {digit}
                                    </button>
                                ))}

                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => pressPin('back')}
                                    className="btn min-h-[3rem] sm:min-h-[3.5rem] border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                                    aria-label="Delete"
                                >
                                    <FiDelete className="h-5 w-5" />
                                </button>

                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => pressPin('0')}
                                    className="btn min-h-[3rem] sm:min-h-[3.5rem] border border-slate-200 bg-slate-50 text-xl font-semibold text-slate-800 hover:bg-slate-100 active:bg-slate-200"
                                >
                                    0
                                </button>

                                <button
                                    type="button"
                                    disabled={busy || pin.length < 4}
                                    onClick={() => dispatch(loginWithPin({ pin }))}
                                    className="btn-primary min-h-[3rem] sm:min-h-[3.5rem]"
                                    aria-label="Sign in"
                                >
                                    {busy ? <Spinner size="sm" /> : <FiArrowRight className="h-5 w-5" />}
                                </button>
                            </div>
                        </>
                    ) : (
                        <form
                            onSubmit={(event) => {
                                event.preventDefault();
                                dispatch(login(form));
                            }}
                            className="space-y-4"
                        >
                            <div>
                                <label className="label">Username</label>
                                <div className="relative">
                                    <FiUser className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        className="input pl-9"
                                        value={form.userName}
                                        onChange={(event) => setForm({ ...form, userName: event.target.value })}
                                        autoComplete="username"
                                        autoFocus
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label">Password</label>
                                <div className="relative">
                                    <FiLock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="password"
                                        className="input pl-9"
                                        value={form.password}
                                        onChange={(event) => setForm({ ...form, password: event.target.value })}
                                        autoComplete="current-password"
                                        required
                                    />
                                </div>
                            </div>

                            <button type="submit" className="btn-primary w-full" disabled={busy}>
                                {busy ? <Spinner size="sm" /> : 'Sign in'}
                            </button>
                        </form>
                    )}

                    <button
                        type="button"
                        onClick={() => { setMode(mode === 'pin' ? 'password' : 'pin'); setPin(''); }}
                        className="mt-4 w-full text-center text-sm text-brand-700 hover:underline"
                    >
                        {mode === 'pin' ? 'Sign in with a username instead' : 'Use a PIN instead'}
                    </button>
                </div>

                <AppFooter tone="dark" className="mt-4 sm:mt-6" />
            </div>
        </div>
    );
};

export default LoginPage;
